import { useState } from "react";
import { useAuth, api } from "@/lib/auth";
import { CONSULTANTS, CONSULT_PRICING } from "@/data/demo";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { HotBadge, LivePulse, SocialProof, LiveNudge, hotFor } from "@/components/Engagement";
import { toast } from "sonner";

export default function Consultants() {
  const { refreshUser } = useAuth();
  const [active, setActive] = useState(null);
  const [booked, setBooked] = useState(null);

  const book = async (c, plan) => {
    try { await api.post("/wallet/topup", { amount: -plan.p }); await refreshUser(); setActive(null); setBooked({ c, plan }); toast.success(`Booked ${c.name} · ${plan.d}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const gcal = (c, plan) => {
    const text = encodeURIComponent(`${plan.d} consultation with ${c.name} (${c.role}) · MFDX`);
    const details = encodeURIComponent(`Booked via MFDX. ${c.role}.`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&details=${details}`;
  };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Book an Expert" subtitle="Practo-style marketplace of vetted consultants. Pay only for the consultation." icon="UserCheck" color="#F97316" />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5" data-testid="consultants-live-strip">
        <LivePulse label="9 MFDs consulting now" />
        <SocialProof icon="Star">4.8 avg rating across experts</SocialProof>
        <SocialProof icon="CalendarCheck">70+ sessions booked this week</SocialProof>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CONSULTANTS.map((c) => (
          <div key={c.name} className="card-mfx p-6 hover-lift" data-testid="consultant-card">
            <div className="flex items-center gap-3">
              <img src={c.img} alt={c.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow" loading="lazy" />
              <div><div className="flex items-center gap-1"><h3 className="font-head font-semibold">{c.name}</h3><Icon name="BadgeCheck" size={14} className="text-blue-500" /></div>
                <div className="text-xs text-gray-400">{c.role}</div></div>
            </div>
            <div className="flex items-center gap-3 mt-3 text-sm">
              <span className="flex items-center gap-1 text-amber-500 font-semibold"><Icon name="Star" size={14} className="fill-amber-400" /> {c.rating}</span>
              <span className="text-gray-400">{c.reviews} reviews</span><span className="text-gray-400">· {c.exp}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">{c.tags.map((t) => <span key={t} className="text-[11px] bg-gray-100 rounded-full px-2 py-0.5 text-gray-500">{t}</span>)}</div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <HotBadge badge={hotFor(c.name).badge} />
              <SocialProof icon="CalendarCheck">{hotFor(c.name).booked} MFDs consulted this month</SocialProof>
            </div>
            <Btn onClick={() => setActive(c)} className="mt-4 w-full justify-center" data-testid="consultant-book-btn"><Icon name="CalendarPlus" size={15} /> Book consultation</Btn>
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setActive(null)}>
          <div className="w-full max-w-md card-mfx p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head font-bold text-lg">Book {active.name}</h3><button onClick={() => setActive(null)} className="text-gray-400"><Icon name="X" size={20} /></button></div>
            <p className="text-sm text-gray-500 mb-4">{active.role} · pay from your MFDX wallet.</p>
            <div className="space-y-2">
              {CONSULT_PRICING.map((p) => (
                <button key={p.d} onClick={() => book(active, p)} data-testid="consultant-plan-btn" className="w-full flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <span className="font-medium">{p.d} consultation</span><span className="font-head font-bold text-blue-600">₹{p.p}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {booked && (
        <div className="fixed inset-0 z-[95] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setBooked(null)}>
          <div className="w-full max-w-md card-mfx p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="consultant-confirm">
            <div className="w-14 h-14 rounded-2xl grad-lime-cyan grid place-items-center mx-auto mb-4"><Icon name="CheckCircle2" size={28} className="text-white" /></div>
            <h3 className="font-head text-xl font-bold">Consultation booked!</h3>
            <p className="text-gray-500 mt-2">Your {booked.plan.d} consultation with <b>{booked.c.name}</b> ({booked.c.role}) is confirmed. ₹{booked.plan.p} was deducted from your wallet.</p>
            <a href={gcal(booked.c, booked.plan)} target="_blank" rel="noreferrer" data-testid="consultant-gcal-link" className="inline-flex items-center gap-2 mt-6 grad-blue-violet text-white rounded-xl px-5 py-3 font-semibold hover-lift"><Icon name="Calendar" size={18} /> Add to Google Calendar</a>
            <button onClick={() => setBooked(null)} className="block mx-auto mt-3 text-sm text-gray-400">Done</button>
          </div>
        </div>
      )}

      <LiveNudge id="consultants" title="Experts get booked out" message="Top-rated consultants fill their calendars early. Grab a slot while they're free." ctaLabel="Got it" />
    </div>
  );
}
